// Lightweight toast surface built on Radix-toast.
//
// Usage:
//   <ToastProvider><App /><Toaster /></ToastProvider>
//   const { toast } = useToast(); toast({ title: 'Saved' });

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { cn } from '@/lib/cn';

export type ToastVariant = 'default' | 'success' | 'danger' | 'warn';

export interface ToastInput {
  title: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ActiveToast extends ToastInput {
  id: number;
}

interface ToastContextValue {
  toast: (t: ToastInput) => void;
  toasts: ActiveToast[];
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((t: ToastInput) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...t, id }]);
  }, []);

  const value = useMemo(
    () => ({ toast, toasts, dismiss }),
    [toast, toasts, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right">{children}</RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): { toast: ToastContextValue['toast'] } {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast called outside <ToastProvider>');
  return { toast: ctx.toast };
}

const variantClass: Record<ToastVariant, string> = {
  default: '',
  success: 'gs-toast--success',
  danger: 'gs-toast--danger',
  warn: 'gs-toast--warn',
};

export function Toaster(): JSX.Element {
  const ctx = useContext(ToastContext);
  if (!ctx) return <RadixToast.Viewport />;
  const { toasts, dismiss } = ctx;
  // Per Radix-toast docs, every <Root> must be a *child* of <Viewport>
  // so the SR-announced aria-live region tracks new toasts correctly.
  // (Previously the Roots were rendered as siblings of the Viewport,
  // which left announcements unreliable on first toast after mount.)
  return (
    <RadixToast.Viewport
      className={cn(
        'fixed bottom-6 right-6 z-[100] flex w-[360px] max-w-full flex-col gap-2 p-0',
        'm-0 list-none outline-none',
      )}
    >
      {toasts.map((t) => (
        <RadixToast.Root
          key={t.id}
          duration={t.durationMs ?? 4500}
          onOpenChange={(open) => {
            if (!open) dismiss(t.id);
          }}
          className={cn('gs-toast', variantClass[t.variant ?? 'default'])}
        >
          <div className="flex-1">
            <RadixToast.Title className="font-display text-[13px] font-semibold leading-[18px]">
              {t.title}
            </RadixToast.Title>
            {t.description ? (
              <RadixToast.Description className="mt-1 text-caption leading-[16px] opacity-90">
                {t.description}
              </RadixToast.Description>
            ) : null}
          </div>
          <RadixToast.Close
            className="text-xs opacity-70 hover:opacity-100"
            aria-label="Close"
          >
            ×
          </RadixToast.Close>
        </RadixToast.Root>
      ))}
    </RadixToast.Viewport>
  );
}
